import { z } from 'zod';
import { paginationQuerySchema } from '../common/pagination.js';
import { checkMobilePhone } from './phone.js';
export { checkMobilePhone } from './phone.js';
/**
 * WhatsApp service contracts (docs/rebuild/whatsapp.md). The platform API
 * (section 2) is consumed by other Al-Musafer platforms; keep it stable.
 */
// ---------------------------------------------------------------------------
// Shared enums
// ---------------------------------------------------------------------------
export const MESSAGE_LANGUAGES = ['ar', 'en'];
export const messageLanguageSchema = z.enum(MESSAGE_LANGUAGES);
/** Wire status of a message, lower-case as documented in the platform API. */
export const MESSAGE_STATUSES = [
    'queued',
    'sending',
    'sent',
    'delivered',
    'read',
    'failed',
];
export const messageStatusSchema = z.enum(MESSAGE_STATUSES);
export const recordStatusSchema = z.enum(['ACTIVE', 'INACTIVE']);
/** What a template's approved Meta header carries (WA-3): nothing, or an image. */
export const TEMPLATE_HEADER_TYPES = ['NONE', 'IMAGE'];
export const templateHeaderTypeSchema = z.enum(TEMPLATE_HEADER_TYPES);
/**
 * The recipient of a message: the E.164 of a mobile number (WA-29, the shared
 * phone rule of customers.md section 9). A landline, a number without its
 * country code or an invalid number is refused.
 */
export const e164PhoneSchema = z
    .string()
    .trim()
    .transform((value, ctx) => {
    const result = checkMobilePhone(value);
    if (result.ok)
        return result.e164;
    ctx.addIssue({
        code: 'custom',
        message: result.problem === 'PHONE_NOT_MOBILE'
            ? 'must be a mobile number; landlines are not allowed'
            : 'must be a valid E.164 mobile number such as +962790000001',
    });
    return z.NEVER;
});
/** The single variable of a verification code template (whatsapp.md section 4.1). */
export const VERIFICATION_CODE_VARIABLE = 'code';
/** What a stored or displayed verification code looks like: never the code itself. */
export const VERIFICATION_CODE_MASK = '******';
/** Code expiry written into the Meta template (Meta shows it in the message). */
export const VERIFICATION_CODE_VALIDITY_MINUTES = 5;
/**
 * The fixed, code-free failure codes of a synchronous verification send
 * (WA-32). They are the whole stored and returned reason: Meta's own text may
 * echo the code, so it is never kept.
 */
export const VERIFICATION_FAILURE_CODES = [
    'PROVIDER_UNAVAILABLE',
    'RECIPIENT_NOT_ON_WHATSAPP',
    'TEMPLATE_NOT_APPROVED',
    'PROVIDER_REJECTED',
];
/** The only connected system that may send a verification code template. */
export const VERIFICATION_CODE_SYSTEM = 'CUSTOMERS';
/** Meta: an authentication code is at most 15 letters or digits. */
export const verificationCodeValueSchema = z
    .string()
    .regex(/^[A-Za-z0-9]{1,15}$/, 'must be 1 to 15 letters or digits');
/**
 * Meta rejects body parameters containing new lines, tabs or more than four
 * consecutive spaces (error 132018).
 */
export const templateVariableValueSchema = z
    .string()
    .min(1)
    .max(1000)
    .refine((v) => v.trim().length > 0, 'must not be blank')
    .refine((v) => !/[\n\r\t]/.test(v), 'must not contain line breaks or tabs')
    .refine((v) => !/ {5,}/.test(v), 'must not contain more than four consecutive spaces');
export const templateKeySchema = z
    .string()
    .trim()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9_]+$/, 'must use lower-case letters, digits and underscores');
export const variableNameSchema = z
    .string()
    .trim()
    .min(1)
    .max(50)
    .regex(/^[a-zA-Z][a-zA-Z0-9_]*$/, 'must start with a letter and use letters, digits and underscores');
export const systemCodeSchema = z
    .string()
    .trim()
    .min(2)
    .max(40)
    .regex(/^[A-Z][A-Z0-9_]*$/, 'must use upper-case letters, digits and underscores');
// ---------------------------------------------------------------------------
// Platform API (server to server) - section 2
// ---------------------------------------------------------------------------
/** Image types Meta accepts for a template image header. */
export const HEADER_IMAGE_CONTENT_TYPES = ['image/png', 'image/jpeg'];
/** At most 1 MB once decoded (WA-3). */
export const HEADER_IMAGE_MAX_BYTES = 1024 * 1024;
/** Decoded byte length of a padded base64 string, without decoding it. */
export function base64DecodedLength(value) {
    const padding = value.endsWith('==') ? 2 : value.endsWith('=') ? 1 : 0;
    return (value.length / 4) * 3 - padding;
}
export const headerImageSchema = z.object({
    contentType: z.enum(HEADER_IMAGE_CONTENT_TYPES),
    dataBase64: z
        .string()
        .min(4)
        // A length check first keeps the regex below off oversized input.
        .max(Math.ceil(HEADER_IMAGE_MAX_BYTES / 3) * 4, 'must be at most 1 MB once decoded')
        .regex(/^[A-Za-z0-9+/]+={0,2}$/, 'must be standard base64')
        .refine((v) => v.length % 4 === 0, 'must be standard padded base64')
        .refine((v) => base64DecodedLength(v) <= HEADER_IMAGE_MAX_BYTES, 'must be at most 1 MB once decoded'),
});
export const sendMessageSchema = z.object({
    templateKey: templateKeySchema,
    language: messageLanguageSchema,
    to: e164PhoneSchema,
    variables: z.record(z.string(), templateVariableValueSchema),
    idempotencyKey: z.string().trim().min(1).max(200),
    /** Required by a template whose header type is IMAGE, refused otherwise (WA-3). */
    headerImage: headerImageSchema.optional(),
});
// ---------------------------------------------------------------------------
// Admin: messages - sections 5.1 and 5.2
// ---------------------------------------------------------------------------
export const messageListQuerySchema = paginationQuerySchema.extend({
    systemId: z.string().trim().min(1).optional(),
    status: messageStatusSchema.optional(),
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    /** Phone search: digits, matched as a substring of the stored E.164 number. */
    phone: z.string().trim().max(20).optional(),
});
// ---------------------------------------------------------------------------
// Admin: templates - sections 4 and 5.3
// ---------------------------------------------------------------------------
const templateFieldsSchema = z.object({
    key: templateKeySchema,
    language: messageLanguageSchema,
    metaName: z
        .string()
        .trim()
        .min(1)
        .max(512)
        .regex(/^[a-z0-9_]+$/, 'must use lower-case letters, digits and underscores'),
    metaLanguage: z
        .string()
        .trim()
        .min(2)
        .max(10)
        .regex(/^[a-z]{2,3}(_[A-Z]{2})?$/, 'must be a Meta language code such as ar or en_US'),
    variables: z
        .array(variableNameSchema)
        .max(20)
        .refine((names) => new Set(names).size === names.length, 'variable names must be unique'),
    bodyText: z.string().trim().max(1024).optional().nullable(),
    /** Omitted on create means NONE; omitted on update keeps the current value. */
    headerType: templateHeaderTypeSchema.optional(),
    /**
     * A Meta AUTHENTICATION template (whatsapp.md section 4.1): off by default,
     * fixed after creation. It has exactly one variable, `code`, and no header.
     */
    verificationCode: z.boolean().optional(),
    descriptionAr: z.string().trim().min(1).max(300),
    descriptionEn: z.string().trim().min(1).max(300),
});
export const createTemplateSchema = templateFieldsSchema.superRefine((value, ctx) => {
    if (!value.verificationCode)
        return;
    if (value.variables.length !== 1 || value.variables[0] !== VERIFICATION_CODE_VARIABLE) {
        ctx.addIssue({
            code: 'custom',
            path: ['variables'],
            message: `a verification code template has exactly one variable, "${VERIFICATION_CODE_VARIABLE}"`,
        });
    }
    if (value.headerType === 'IMAGE') {
        ctx.addIssue({
            code: 'custom',
            path: ['headerType'],
            message: 'a verification code template has no image header',
        });
    }
});
/** key, language and the verification code flag are fixed after creation. */
export const updateTemplateSchema = templateFieldsSchema
    .omit({ key: true, language: true, verificationCode: true })
    .partial();
export const changeRecordStatusSchema = z.object({ status: recordStatusSchema });
export const templateListQuerySchema = paginationQuerySchema.extend({
    status: recordStatusSchema.optional(),
    language: messageLanguageSchema.optional(),
});
// ---------------------------------------------------------------------------
// Admin: connected systems - section 5.4
// ---------------------------------------------------------------------------
export const createSystemSchema = z.object({
    code: systemCodeSchema,
    nameAr: z.string().trim().min(1).max(120),
    nameEn: z.string().trim().min(1).max(120),
});
/** code is fixed after creation. */
export const updateSystemSchema = createSystemSchema.omit({ code: true }).partial();
export const systemListQuerySchema = paginationQuerySchema.extend({
    status: recordStatusSchema.optional(),
});
//# sourceMappingURL=contract.js.map